import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, tokenStore, type ApiError } from '../../api/client';
import { Alert } from '../../components/Field';
import { Tile } from '../../components/Tile';
import { BarChart } from '../../components/BarChart';
import { useAuth } from '../../auth/AuthContext';
import { money } from '../../lib/format';

// ------------------------------------------------------------------- types
type State = 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'PAUSED' | 'EXPIRED' | 'EXHAUSTED' | 'DISABLED';
type DiscountType = 'FREE_ACCESS' | 'PERCENTAGE' | 'FIXED_AMOUNT';
type ScopeType = 'ALL' | 'COURSE' | 'CATEGORY' | 'PRODUCT' | 'PATH' | 'PLAN';

type Audience = 'ALL' | 'NEW_USERS' | 'EXISTING_USERS';

interface PromoCode {
  id: string;
  code: string;
  description: string | null;
  campaign: { id: string; name: string } | null;
  maxPerOrganization: number | null;
  eligibility: {
    audience: Audience;
    newUserDays: number;
    firstPurchaseOnly: boolean;
    emails: string[];
    domains: string[];
    organizationIds: string[];
  };
  /** The discount terms freeze once anyone has redeemed the code. */
  termsLocked: boolean;
  discountType: DiscountType;
  discountValue: number;
  maxDiscountMinor: number | null;
  currency: string;
  validFrom: string;
  validUntil: string | null;
  timezone: string;
  maxRedemptions: number | null;
  maxPerUser: number;
  minOrderMinor: number;
  scope: { type: ScopeType | 'ORGANIZATION'; ids: string[] };
  status: string;
  state: State;
  redemptionCount: number;
  remaining: number | null;
  batchId: string | null;
  grantDays: number | null;
}

interface Stats {
  currency: string;
  totalCodes: number;
  byState: Record<State, number>;
  totalRedemptions: number;
  totalDiscountMinor: number;
  freeRedemptions: number;
  promoRevenueMinor: number;
}

interface Option {
  id: string;
  label: string;
  priceMinor?: number;
  currency?: string;
}
type Options = Record<Exclude<ScopeType, 'ALL'> | 'ORGANIZATION' | 'CAMPAIGN', Option[]>;

interface Campaign {
  id: string;
  name: string;
  description: string | null;
  owner: { id: string; name: string } | null;
  startsAt: string | null;
  endsAt: string | null;
  archived: boolean;
  codes: number;
  redeemedCodes: number;
  remainingCodes: number;
  redemptions: number;
  courses: number;
  discountMinor: number;
  revenueMinor: number;
}

interface Redemption {
  userEmail: string | null;
  userName: string | null;
  orderId: string | null;
  items: string[];
  grossMinor: number | null;
  discountMinor: number;
  netMinor: number | null;
  currency: string;
  status: string;
  redeemedAt: string;
}

// ----------------------------------------------------------------- helpers
const STATE_LABEL: Record<State, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  EXPIRED: 'Expired',
  EXHAUSTED: 'Fully redeemed',
  DISABLED: 'Disabled',
};

// Mapped onto the pill colours the rest of the admin already uses.
const STATE_PILL: Record<State, string> = {
  DRAFT: 'draft',
  SCHEDULED: 'approved',
  ACTIVE: 'active',
  PAUSED: 'pending',
  EXPIRED: 'inactive',
  EXHAUSTED: 'inactive',
  DISABLED: 'suspended',
};

const SCOPE_LABEL: Record<ScopeType, string> = {
  ALL: 'Entire platform',
  COURSE: 'Specific courses',
  CATEGORY: 'Category',
  PRODUCT: 'Product',
  PATH: 'Learning path',
  PLAN: 'Subscription plan',
};

const TIMEZONES = [
  'Asia/Kolkata',
  'UTC',
  'Asia/Dubai',
  'Asia/Singapore',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
  'Australia/Sydney',
];

/** "1 course", "3 categories", "2 learning paths". */
function scopeCount(type: string, count: number): string {
  const nouns: Record<string, [string, string]> = {
    COURSE: ['course', 'courses'],
    CATEGORY: ['category', 'categories'],
    PRODUCT: ['product', 'products'],
    PATH: ['learning path', 'learning paths'],
    PLAN: ['plan', 'plans'],
  };
  const [one, many] = nouns[type] ?? [type.toLowerCase(), type.toLowerCase()];
  return `${count} ${count === 1 ? one : many}`;
}

function discountText(
  code: Pick<PromoCode, 'discountType' | 'discountValue' | 'maxDiscountMinor' | 'currency'>,
): string {
  if (code.discountType === 'FREE_ACCESS') return '100% (free)';
  if (code.discountType === 'PERCENTAGE') {
    return code.maxDiscountMinor
      ? `${code.discountValue}% up to ${money(code.maxDiscountMinor, code.currency)}`
      : `${code.discountValue}%`;
  }
  return `${money(code.discountValue, code.currency)} off`;
}

/** A moment shown in the timezone the code was set in, e.g. "1 Oct 2026, 00:00 IST". */
function when(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-IN', {
      timeZone,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleString();
  }
}

/** Minutes a timezone is ahead of UTC at a given instant. */
function offsetMinutes(instant: number, timeZone: string): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date(instant))
      .map((part) => [part.type, part.value]),
  );
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return Math.round((asUtc - instant) / 60000);
}

/**
 * "2026-10-01" + "00:00" in Asia/Kolkata → the UTC instant, as ISO. Computed
 * twice so a date near a daylight-saving change lands on the right side of it.
 */
function zonedToIso(date: string, time: string, timeZone: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const wall = Date.UTC(y!, m! - 1, d!, hh!, mm!);
  let instant = wall - offsetMinutes(wall, timeZone) * 60000;
  instant = wall - offsetMinutes(instant, timeZone) * 60000;
  return new Date(instant).toISOString();
}

/** The reverse, for prefilling a duplicate: UTC ISO → date and time in a zone. */
function isoToZoned(iso: string, timeZone: string): { date: string; time: string } {
  const instant = new Date(iso).getTime();
  const local = new Date(instant + offsetMinutes(instant, timeZone) * 60000).toISOString();
  return { date: local.slice(0, 10), time: local.slice(11, 16) };
}

async function download(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(`/api/v1${path}`, {
    headers: tokenStore.access ? { Authorization: `Bearer ${tokenStore.access}` } : {},
  });
  if (!response.ok) throw new Error(`The export could not be downloaded (${response.status}).`);
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// -------------------------------------------------------------- the form
type CodeMode = 'manual' | 'generate' | 'bulk';

/** '' = no campaign, NEW_CAMPAIGN = type a new name, otherwise a campaign id. */
const NEW_CAMPAIGN = '__new__';

interface FormState {
  campaignId: string;
  campaignName: string;
  mode: CodeMode;
  code: string;
  prefix: string;
  codeLength: number;
  count: number;
  description: string;
  discountType: DiscountType;
  percent: string;
  fixedRupees: string;
  maxDiscountRupees: string;
  grantDays: string;
  scopeType: ScopeType;
  scopeIds: string[];
  unlimited: boolean;
  maxRedemptions: string;
  maxPerUser: string;
  minOrderRupees: string;
  startDate: string;
  startTime: string;
  hasEnd: boolean;
  endDate: string;
  endTime: string;
  timezone: string;
  // who may redeem
  audience: Audience;
  newUserDays: string;
  firstPurchaseOnly: boolean;
  emailsText: string;
  domainsText: string;
  orgIds: string[];
  orgLimit: string;
}

/** One entry per line or comma, trimmed, empties dropped. */
function listFrom(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function today(timeZone: string): string {
  return isoToZoned(new Date().toISOString(), timeZone).date;
}

function blankForm(): FormState {
  return {
    campaignId: '',
    campaignName: '',
    audience: 'ALL',
    newUserDays: '30',
    firstPurchaseOnly: false,
    emailsText: '',
    domainsText: '',
    orgIds: [],
    orgLimit: '',
    mode: 'manual',
    code: '',
    prefix: 'SKX',
    codeLength: 6,
    count: 100,
    description: '',
    discountType: 'PERCENTAGE',
    percent: '25',
    fixedRupees: '',
    maxDiscountRupees: '',
    grantDays: '',
    scopeType: 'ALL',
    scopeIds: [],
    unlimited: false,
    maxRedemptions: '1000',
    maxPerUser: '1',
    minOrderRupees: '',
    startDate: today('Asia/Kolkata'),
    startTime: '00:00',
    hasEnd: true,
    endDate: '',
    endTime: '23:59',
    timezone: 'Asia/Kolkata',
  };
}

/** Prefill from an existing code — for "Duplicate" and "Edit". */
function formFrom(code: PromoCode): FormState {
  const start = isoToZoned(code.validFrom, code.timezone);
  const end = code.validUntil ? isoToZoned(code.validUntil, code.timezone) : null;
  return {
    ...blankForm(),
    campaignId: code.campaign?.id ?? '',
    audience: code.eligibility.audience,
    newUserDays: String(code.eligibility.newUserDays),
    firstPurchaseOnly: code.eligibility.firstPurchaseOnly,
    emailsText: code.eligibility.emails.join('\n'),
    domainsText: code.eligibility.domains.join('\n'),
    orgIds: code.eligibility.organizationIds,
    orgLimit: code.maxPerOrganization ? String(code.maxPerOrganization) : '',
    description: code.description ?? '',
    discountType: code.discountType,
    percent: code.discountType === 'PERCENTAGE' ? String(code.discountValue) : '25',
    fixedRupees: code.discountType === 'FIXED_AMOUNT' ? String(code.discountValue / 100) : '',
    maxDiscountRupees: code.maxDiscountMinor ? String(code.maxDiscountMinor / 100) : '',
    grantDays: code.grantDays ? String(code.grantDays) : '',
    scopeType: code.scope.type === 'ORGANIZATION' ? 'ALL' : code.scope.type,
    scopeIds: code.scope.ids,
    unlimited: code.maxRedemptions === null,
    maxRedemptions: code.maxRedemptions ? String(code.maxRedemptions) : '1000',
    maxPerUser: String(code.maxPerUser),
    minOrderRupees: code.minOrderMinor ? String(code.minOrderMinor / 100) : '',
    startDate: start.date,
    startTime: start.time,
    hasEnd: end !== null,
    endDate: end?.date ?? '',
    endTime: end?.time ?? '23:59',
    timezone: code.timezone,
  };
}

const STEPS = [
  'Basics',
  'Discount',
  'Applies to',
  'Who can redeem',
  'Limits',
  'Validity',
  'Review',
] as const;
const STEP = { basics: 0, discount: 1, scope: 2, who: 3, limits: 4, validity: 5, review: 6 };

/** Every problem with the form at once, keyed by the step that owns it. */
function problems(form: FormState, editing: boolean): Record<number, string[]> {
  const out: Record<number, string[]> = {};
  const add = (step: number, message: string) => (out[step] ??= []).push(message);
  const code = form.code.trim().toUpperCase();

  if (!editing && form.mode === 'manual' && !/^[A-Z0-9]+(?:-[A-Z0-9]+)*$/.test(code)) {
    add(0, 'Enter a code: letters and digits, optionally separated by single hyphens.');
  } else if (!editing && form.mode === 'manual' && (code.length < 3 || code.length > 60)) {
    add(0, 'A code is 3–60 characters.');
  }
  if (form.campaignId === NEW_CAMPAIGN && !form.campaignName.trim()) {
    add(0, 'Name the new campaign, or choose an existing one.');
  }
  if (form.mode === 'bulk' && (form.count < 1 || form.count > 5000)) {
    add(0, 'Generate between 1 and 5,000 codes at a time.');
  }

  if (form.discountType === 'PERCENTAGE') {
    const pct = Number(form.percent);
    if (!Number.isInteger(pct) || pct < 1 || pct > 100)
      add(1, 'A percentage is a whole number from 1 to 100.');
  }
  if (form.discountType === 'FIXED_AMOUNT' && !(Number(form.fixedRupees) > 0)) {
    add(1, 'Enter the amount to take off.');
  }

  if (form.scopeType !== 'ALL' && form.scopeIds.length === 0) {
    add(2, `Choose at least one ${SCOPE_LABEL[form.scopeType].toLowerCase()}.`);
  }

  if (form.audience !== 'ALL' && !(Number(form.newUserDays) >= 1)) {
    add(STEP.who, 'Say how many days an account counts as new.');
  }
  const badEmail = listFrom(form.emailsText).find(
    (email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
  );
  if (badEmail) add(STEP.who, `"${badEmail}" is not an email address.`);
  const badDomain = listFrom(form.domainsText).find(
    (domain) => !/^@?[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(domain),
  );
  if (badDomain) add(STEP.who, `"${badDomain}" is not a domain, e.g. acme.com.`);

  if (!form.unlimited && !(Number(form.maxRedemptions) >= 1))
    add(STEP.limits, 'Set a total usage limit, or choose unlimited.');
  if (!(Number(form.maxPerUser) >= 0))
    add(STEP.limits, 'Set how many times one person may use it (0 = no limit).');
  if (form.orgLimit.trim() && !(Number(form.orgLimit) >= 1))
    add(STEP.limits, 'The per-organization limit must be at least 1, or left empty.');

  if (!form.startDate || !form.startTime) add(STEP.validity, 'Choose when the code starts.');
  if (form.hasEnd) {
    if (!form.endDate || !form.endTime)
      add(STEP.validity, 'Choose when the code ends, or remove the end date.');
    else if (
      form.startDate &&
      zonedToIso(form.endDate, form.endTime, form.timezone) <=
        zonedToIso(form.startDate, form.startTime, form.timezone)
    ) {
      add(STEP.validity, 'The end must come after the start.');
    }
  }
  return out;
}

/** Plain-English summary of who may redeem, for the review step and details. */
function whoText(
  eligibility: Pick<PromoCode['eligibility'], 'audience' | 'newUserDays' | 'firstPurchaseOnly'> & {
    emails: number;
    domains: string[];
    organizations: string[];
  },
): string {
  const parts: string[] = [];
  if (eligibility.audience === 'NEW_USERS')
    parts.push(`accounts under ${eligibility.newUserDays} days old`);
  if (eligibility.audience === 'EXISTING_USERS')
    parts.push(`accounts over ${eligibility.newUserDays} days old`);
  if (eligibility.firstPurchaseOnly) parts.push('first paid purchase only');
  const lists: string[] = [];
  if (eligibility.emails)
    lists.push(`${eligibility.emails} named ${eligibility.emails === 1 ? 'person' : 'people'}`);
  if (eligibility.domains.length) lists.push(`@${eligibility.domains.join(', @')}`);
  if (eligibility.organizations.length) lists.push(eligibility.organizations.join(', '));
  if (lists.length) parts.push(`limited to ${lists.join(' or ')}`);
  return parts.length ? parts.join(' · ') : 'Everyone';
}

/**
 * The request body. A create omits what is not set; an edit sends null for
 * "no limit", "no end date" and "no campaign", so clearing a value works.
 */
function toPayload(form: FormState, status: 'DRAFT' | 'ACTIVE', editing: boolean) {
  const rupees = (value: string) => Math.round(Number(value) * 100);
  const discountValue =
    form.discountType === 'PERCENTAGE'
      ? Number(form.percent)
      : form.discountType === 'FIXED_AMOUNT'
        ? rupees(form.fixedRupees)
        : 100;
  const clear = editing ? null : undefined;

  const campaign =
    form.campaignId === NEW_CAMPAIGN
      ? { campaign: form.campaignName.trim() }
      : form.campaignId
        ? { campaignId: form.campaignId }
        : editing
          ? { campaignId: '' }
          : {};

  return {
    ...campaign,
    description: form.description.trim(),
    discountType: form.discountType,
    discountValue,
    maxDiscountMinor:
      form.discountType === 'PERCENTAGE' && Number(form.maxDiscountRupees) > 0
        ? rupees(form.maxDiscountRupees)
        : clear,
    grantDays:
      form.discountType === 'FREE_ACCESS' && Number(form.grantDays) > 0
        ? Math.round(Number(form.grantDays))
        : clear,
    currency: 'INR',
    scopeType: form.scopeType,
    scopeIds: form.scopeType !== 'ALL' ? form.scopeIds : [],
    maxRedemptions: form.unlimited ? clear : Math.round(Number(form.maxRedemptions)),
    maxPerUser: Math.round(Number(form.maxPerUser)),
    maxPerOrganization: form.orgLimit.trim() ? Math.round(Number(form.orgLimit)) : clear,
    minOrderMinor: Number(form.minOrderRupees) > 0 ? rupees(form.minOrderRupees) : 0,
    validFrom: zonedToIso(form.startDate, form.startTime, form.timezone),
    validUntil: form.hasEnd ? zonedToIso(form.endDate, form.endTime, form.timezone) : clear,
    timezone: form.timezone,
    audience: form.audience,
    newUserDays: Math.max(1, Math.round(Number(form.newUserDays) || 30)),
    firstPurchaseOnly: form.firstPurchaseOnly,
    eligibleEmails: listFrom(form.emailsText),
    eligibleDomains: listFrom(form.domainsText).map((domain) =>
      domain.replace(/^@/, '').toLowerCase(),
    ),
    eligibleTenantIds: form.orgIds,
    ...(editing ? {} : { status }),
  };
}

// ============================================================== the page
/**
 * Marketing → Promo codes (promo spec §1, §2, §19–§22, §28–§29).
 *
 * The page only ever displays what the API computed — states, counts and
 * totals are all derived on the server, so this screen and the checkout can
 * never disagree about whether a code works.
 */
export function PromoCodes() {
  const { can } = useAuth();
  const canCreate = can('COUPON_CREATE');
  const canChange = can('COUPON_DISABLE');
  const canExport = can('COUPON_EXPORT');
  const canEditCodes = can('COUPON_UPDATE');

  const [stats, setStats] = useState<Stats | null>(null);
  const [rows, setRows] = useState<PromoCode[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [campaignList, setCampaignList] = useState<Option[]>([]);
  const [tab, setTab] = useState<'codes' | 'campaigns' | 'reports'>('codes');
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The form is either creating (no code) or editing an existing one.
  const [creating, setCreating] = useState<{ form: FormState; editing: PromoCode | null } | null>(
    null,
  );
  const [batchResult, setBatchResult] = useState<{ batchId: string; codes: string[] } | null>(null);
  const [viewing, setViewing] = useState<PromoCode | null>(null);

  const pageSize = 20;

  const loadCampaignNames = useCallback(() => {
    api<{ items: Campaign[] }>('/admin/commerce/campaigns')
      .then((result) => setCampaignList(result.items.map((c) => ({ id: c.id, label: c.name }))))
      .catch(() => undefined);
  }, []);
  useEffect(loadCampaignNames, [loadCampaignNames]);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (q.trim()) params.set('q', q.trim());
    if (stateFilter) params.set('state', stateFilter);
    if (typeFilter) params.set('type', typeFilter);
    if (campaignFilter) params.set('campaignId', campaignFilter);
    api<{ items: PromoCode[]; total: number }>(`/admin/commerce/coupons?${params.toString()}`)
      .then((result) => {
        setRows(result.items);
        setTotal(result.total);
      })
      .catch((caught) => setError(caught as ApiError));
    api<Stats>('/admin/commerce/coupons/stats')
      .then(setStats)
      .catch(() => undefined);
  }, [page, q, stateFilter, typeFilter, campaignFilter]);

  // Searching waits for a pause in typing rather than firing on every key.
  useEffect(() => {
    const timer = window.setTimeout(load, q ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [load, q]);

  async function setStatus(code: PromoCode, status: 'ACTIVE' | 'PAUSED' | 'DISABLED') {
    setError(null);
    try {
      await api(`/admin/commerce/coupons/${code.id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      });
      const verb = { ACTIVE: 'activated', PAUSED: 'paused', DISABLED: 'disabled' }[status];
      setNotice(`${code.code} was ${verb}.`);
      load();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Promo codes</h1>
        <p className="page__lede">
          Create and manage promotional codes and campaigns: free courses, percentage and fixed
          discounts, with targeting, eligibility, usage limits and validity windows. Every price is
          recalculated on the server at checkout.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
      {notice ? <Alert kind="success" title={notice} /> : null}

      <div className="segmented" role="tablist" aria-label="Promo code views">
        <button
          role="tab"
          aria-selected={tab === 'codes'}
          className={`segmented__item${tab === 'codes' ? ' is-on' : ''}`}
          onClick={() => setTab('codes')}
        >
          Promo codes
        </button>
        <button
          role="tab"
          aria-selected={tab === 'campaigns'}
          className={`segmented__item${tab === 'campaigns' ? ' is-on' : ''}`}
          onClick={() => setTab('campaigns')}
        >
          Campaigns
        </button>
        <button
          role="tab"
          aria-selected={tab === 'reports'}
          className={`segmented__item${tab === 'reports' ? ' is-on' : ''}`}
          onClick={() => setTab('reports')}
        >
          Reports
        </button>
      </div>

      {tab === 'reports' ? (
        <PromoReports campaigns={campaignList} canExport={canExport} onError={setError} />
      ) : tab === 'campaigns' ? (
        <Campaigns
          canCreate={canCreate}
          canEdit={can('COUPON_UPDATE')}
          onError={setError}
          onChanged={loadCampaignNames}
          onShowCodes={(campaign) => {
            setCampaignFilter(campaign.id);
            setPage(1);
            setTab('codes');
          }}
        />
      ) : (
        <>
          <div className="tiles tiles--promo">
            <Tile
              tone="navy"
              icon="⌗"
              label="Total codes"
              value={stats ? stats.totalCodes.toLocaleString() : '—'}
            />
            <Tile
              tone="teal"
              icon="●"
              label="Active"
              value={stats ? stats.byState.ACTIVE.toLocaleString() : '—'}
              note={
                stats ? `${stats.byState.PAUSED} paused · ${stats.byState.DRAFT} draft` : undefined
              }
            />
            <Tile
              tone="azure"
              icon="◷"
              label="Scheduled"
              value={stats ? stats.byState.SCHEDULED.toLocaleString() : '—'}
            />
            <Tile
              tone="indigo"
              icon="⌛"
              label="Expired"
              value={stats ? stats.byState.EXPIRED.toLocaleString() : '—'}
              note={stats ? `${stats.byState.EXHAUSTED} fully redeemed` : undefined}
            />
            <Tile
              tone="gold"
              icon="✓"
              label="Redemptions"
              value={stats ? stats.totalRedemptions.toLocaleString() : '—'}
            />
            <Tile
              tone="navy"
              icon="₹"
              label="Total discount given"
              value={stats ? money(stats.totalDiscountMinor, stats.currency) : '—'}
            />
            <Tile
              tone="teal"
              icon="🎓"
              label="Free enrollments"
              value={stats ? stats.freeRedemptions.toLocaleString() : '—'}
              note="via 100% codes"
            />
            <Tile
              tone="gold"
              icon="↗"
              label="Revenue with promotions"
              value={stats ? money(stats.promoRevenueMinor, stats.currency) : '—'}
              note="paid orders using a code"
            />
          </div>

          {batchResult ? (
            <div className="card">
              <div className="card__head">
                <span className="card__title">
                  {batchResult.codes.length.toLocaleString()} codes generated
                </span>
                <button className="iconbtn" onClick={() => setBatchResult(null)}>
                  Close
                </button>
              </div>
              <div className="card__body">
                <Alert
                  kind="warning"
                  title="Download these now."
                  items={[
                    'The full list is shown only once. Afterwards it is available only through the export, which is recorded in the audit log.',
                  ]}
                />
                <div className="codes">
                  {batchResult.codes.slice(0, 30).map((code) => (
                    <code key={code} className="mono">
                      {code}
                    </code>
                  ))}
                  {batchResult.codes.length > 30 ? (
                    <span className="muted">
                      …and {(batchResult.codes.length - 30).toLocaleString()} more
                    </span>
                  ) : null}
                </div>
                {canExport ? (
                  <div className="certcard__actions">
                    <button
                      className="btn btn--sm"
                      onClick={() =>
                        void download(
                          `/admin/commerce/coupons/batches/${batchResult.batchId}/export`,
                          'promo-codes.csv',
                        ).catch((caught) =>
                          setError({ message: (caught as Error).message } as ApiError),
                        )
                      }
                    >
                      Download CSV
                    </button>
                    <button
                      className="btn btn--sm btn--ghost"
                      onClick={() =>
                        void download(
                          `/admin/commerce/coupons/batches/${batchResult.batchId}/export?format=xlsx`,
                          'promo-codes.xlsx',
                        ).catch((caught) =>
                          setError({ message: (caught as Error).message } as ApiError),
                        )
                      }
                    >
                      Download Excel
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {creating ? (
            <CreateWizard
              key={creating.editing?.id ?? 'new'}
              initial={creating.form}
              editing={creating.editing}
              onCancel={() => setCreating(null)}
              onCreated={(message, batch) => {
                setCreating(null);
                setNotice(message);
                if (batch) setBatchResult(batch);
                if (!creating.editing) setPage(1);
                load();
                loadCampaignNames();
              }}
            />
          ) : null}

          {viewing ? (
            <Details
              code={viewing}
              canExport={canExport}
              onClose={() => setViewing(null)}
              onError={setError}
            />
          ) : null}

          <div className="card">
            <div className="card__head">
              <span className="card__title">All promo codes</span>
              {canCreate && !creating ? (
                <button
                  className="btn btn--sm"
                  onClick={() => {
                    setNotice(null);
                    setCreating({ form: blankForm(), editing: null });
                  }}
                >
                  New promo code
                </button>
              ) : null}
            </div>

            <div className="toolbar">
              <input
                className="input toolbar__search"
                placeholder="Search code, campaign or description"
                aria-label="Search promo codes"
                value={q}
                onChange={(event) => {
                  setPage(1);
                  setQ(event.target.value);
                }}
              />
              <select
                className="input"
                aria-label="Filter by status"
                value={stateFilter}
                onChange={(event) => {
                  setPage(1);
                  setStateFilter(event.target.value);
                }}
              >
                <option value="">All statuses</option>
                {(Object.keys(STATE_LABEL) as State[]).map((state) => (
                  <option key={state} value={state}>
                    {STATE_LABEL[state]}
                  </option>
                ))}
              </select>
              <select
                className="input"
                aria-label="Filter by discount type"
                value={typeFilter}
                onChange={(event) => {
                  setPage(1);
                  setTypeFilter(event.target.value);
                }}
              >
                <option value="">All discount types</option>
                <option value="FREE_ACCESS">100% free</option>
                <option value="PERCENTAGE">Percentage</option>
                <option value="FIXED_AMOUNT">Fixed amount</option>
              </select>
              <select
                className="input"
                aria-label="Filter by campaign"
                value={campaignFilter}
                onChange={(event) => {
                  setPage(1);
                  setCampaignFilter(event.target.value);
                }}
              >
                <option value="">All campaigns</option>
                {campaignList.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.label}
                  </option>
                ))}
              </select>
            </div>

            {!rows ? (
              <div className="empty">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="empty">
                No promo codes match.
                <br />
                <span className="muted">
                  {q || stateFilter || typeFilter
                    ? 'Try clearing the filters.'
                    : 'Create one to get started.'}
                </span>
              </div>
            ) : (
              <div className="tablewrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Campaign</th>
                      <th>Discount</th>
                      <th>Applies to</th>
                      <th>Usage</th>
                      <th>Validity</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <strong className="mono">{row.code}</strong>
                          {row.description ? (
                            <div className="muted promo-row__desc">{row.description}</div>
                          ) : null}
                        </td>
                        <td>{row.campaign?.name ?? <span className="muted">—</span>}</td>
                        <td>{discountText(row)}</td>
                        <td>
                          {row.scope.type === 'ALL'
                            ? 'Entire platform'
                            : scopeCount(row.scope.type, row.scope.ids.length)}
                        </td>
                        <td>
                          {row.redemptionCount.toLocaleString()} /{' '}
                          {row.maxRedemptions === null ? '∞' : row.maxRedemptions.toLocaleString()}
                        </td>
                        <td className="promo-row__when">
                          {when(row.validFrom, row.timezone)}
                          <br />
                          <span className="muted">
                            → {row.validUntil ? when(row.validUntil, row.timezone) : 'no end date'}
                          </span>
                        </td>
                        <td>
                          <span className={`pill pill--${STATE_PILL[row.state]}`}>
                            {STATE_LABEL[row.state]}
                          </span>
                        </td>
                        <td>
                          <div className="rowactions">
                            <button className="iconbtn" onClick={() => setViewing(row)}>
                              View
                            </button>
                            {canChange && (row.status === 'DRAFT' || row.status === 'PAUSED') ? (
                              <button
                                className="iconbtn"
                                onClick={() => void setStatus(row, 'ACTIVE')}
                              >
                                Activate
                              </button>
                            ) : null}
                            {canChange && (row.state === 'ACTIVE' || row.state === 'SCHEDULED') ? (
                              <button
                                className="iconbtn"
                                onClick={() => void setStatus(row, 'PAUSED')}
                              >
                                Pause
                              </button>
                            ) : null}
                            {canChange && row.status !== 'DISABLED' ? (
                              <button
                                className="iconbtn"
                                onClick={() => void setStatus(row, 'DISABLED')}
                              >
                                Disable
                              </button>
                            ) : null}
                            {canEditCodes && row.status !== 'DISABLED' ? (
                              <button
                                className="iconbtn"
                                onClick={() => {
                                  setNotice(null);
                                  setCreating({ form: formFrom(row), editing: row });
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                              >
                                Edit
                              </button>
                            ) : null}
                            {canCreate ? (
                              <button
                                className="iconbtn"
                                onClick={() => {
                                  setNotice(null);
                                  setCreating({ form: formFrom(row), editing: null });
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                              >
                                Duplicate
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {total > pageSize ? (
              <div className="pager">
                <button
                  className="iconbtn"
                  disabled={page <= 1}
                  onClick={() => setPage((value) => value - 1)}
                >
                  ← Previous
                </button>
                <span className="muted">
                  Page {page} of {pages} · {total.toLocaleString()} codes
                </span>
                <button
                  className="iconbtn"
                  disabled={page >= pages}
                  onClick={() => setPage((value) => value + 1)}
                >
                  Next →
                </button>
              </div>
            ) : null}
          </div>
        </>
      )}
    </>
  );
}

// ========================================================= create wizard
function CreateWizard({
  initial,
  editing,
  onCancel,
  onCreated,
}: {
  initial: FormState;
  /** The code being edited, or null when creating. */
  editing: PromoCode | null;
  onCancel: () => void;
  onCreated: (message: string, batch?: { batchId: string; codes: string[] }) => void;
}) {
  const isEdit = editing !== null;
  const locked = editing?.termsLocked ?? false;
  const [form, setForm] = useState<FormState>(initial);
  const [step, setStep] = useState(0);
  const [options, setOptions] = useState<Options | null>(null);
  const [pickerFilter, setPickerFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    api<Options>('/admin/commerce/coupons/options')
      .then(setOptions)
      .catch((caught) => setError(caught as ApiError));
  }, []);

  const issues = useMemo(() => problems(form, isEdit), [form, isEdit]);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  function next() {
    if (issues[step]?.length) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setStep((value) => Math.min(value + 1, STEPS.length - 1));
  }

  async function submit(status: 'DRAFT' | 'ACTIVE') {
    const firstBad = Object.keys(issues).map(Number).sort()[0];
    if (firstBad !== undefined) {
      setStep(firstBad);
      setShowErrors(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = toPayload(form, status, isEdit);
      if (editing) {
        const saved = await api<PromoCode>(`/admin/commerce/coupons/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        onCreated(`${saved.code} was updated.`);
      } else if (form.mode === 'bulk') {
        const result = await api<{ batchId: string; created: number; codes: string[] }>(
          '/admin/commerce/coupons/bulk',
          {
            method: 'POST',
            body: JSON.stringify({
              ...payload,
              count: form.count,
              prefix: form.prefix,
              codeLength: form.codeLength,
            }),
          },
        );
        onCreated(`${result.created.toLocaleString()} promo codes were generated.`, {
          batchId: result.batchId,
          codes: result.codes,
        });
      } else {
        const created = await api<PromoCode>('/admin/commerce/coupons', {
          method: 'POST',
          body: JSON.stringify(
            form.mode === 'generate'
              ? { ...payload, generate: true, prefix: form.prefix, codeLength: form.codeLength }
              : { ...payload, code: form.code.trim().toUpperCase() },
          ),
        });
        onCreated(
          status === 'DRAFT'
            ? `${created.code} was saved as a draft.`
            : `${created.code} was created and is ${created.state === 'SCHEDULED' ? 'scheduled' : 'live'}.`,
        );
      }
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  const scopeOptions = form.scopeType !== 'ALL' && options ? options[form.scopeType] : [];
  const filtered = scopeOptions.filter((option) =>
    option.label.toLowerCase().includes(pickerFilter.trim().toLowerCase()),
  );
  const chosenLabels = scopeOptions
    .filter((option) => form.scopeIds.includes(option.id))
    .map((option) => option.label);

  const stepErrors = showErrors ? (issues[step] ?? []) : [];

  return (
    <div className="card wizard">
      <div className="card__head">
        <span className="card__title">
          {editing ? (
            <>
              Edit <span className="mono">{editing.code}</span>
            </>
          ) : (
            'New promo code'
          )}
        </span>
        <button className="iconbtn" onClick={onCancel}>
          Cancel
        </button>
      </div>

      <ol className="wizard__steps">
        {STEPS.map((label, index) => (
          <li key={label}>
            <button
              type="button"
              className={`wizard__step${index === step ? ' is-current' : ''}${issues[index]?.length ? ' has-issue' : ''}`}
              onClick={() => {
                setShowErrors(false);
                setStep(index);
              }}
              aria-current={index === step ? 'step' : undefined}
            >
              <span className="wizard__num">{index + 1}</span> {label}
            </button>
          </li>
        ))}
      </ol>

      <div className="card__body">
        {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
        {stepErrors.length ? (
          <Alert kind="warning" title="Please fix the following" items={stepErrors} />
        ) : null}

        {step === 0 ? (
          <>
            <div className="field__row">
              <Field label="Campaign" hint="Optional. Groups codes for search and reporting.">
                <select
                  className="field__input"
                  value={form.campaignId}
                  onChange={(e) => set('campaignId', e.target.value)}
                >
                  <option value="">No campaign</option>
                  {(options?.CAMPAIGN ?? []).map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.label}
                    </option>
                  ))}
                  <option value={NEW_CAMPAIGN}>+ New campaign…</option>
                </select>
              </Field>
              {form.campaignId === NEW_CAMPAIGN ? (
                <Field label="New campaign name" hint="e.g. AI Career Launch 2026">
                  <input
                    className="field__input"
                    value={form.campaignName}
                    onChange={(e) => set('campaignName', e.target.value)}
                    maxLength={160}
                    autoFocus
                  />
                </Field>
              ) : null}
            </div>
            {isEdit ? (
              <p className="field__hint">
                The code itself cannot change once created — it may already be printed or shared.
              </p>
            ) : null}
            <fieldset className="choices" hidden={isEdit}>
              <legend className="field__label">Code</legend>
              {(
                [
                  ['manual', 'Type a code', 'e.g. AI100FREE'],
                  ['generate', 'Generate one secure code', 'e.g. SKX-7K92PQ'],
                  ['bulk', 'Generate many unique codes', 'For campaigns and partners'],
                ] as const
              ).map(([value, label, hint]) => (
                <label key={value} className={`choice${form.mode === value ? ' is-on' : ''}`}>
                  <input
                    type="radio"
                    name="mode"
                    checked={form.mode === value}
                    onChange={() => set('mode', value)}
                  />
                  <span>
                    <strong>{label}</strong>
                    <span className="muted">{hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            {isEdit ? null : form.mode === 'manual' ? (
              <Field
                label="Promo code"
                hint="Letters and digits, optionally separated by hyphens. Customers type it case-insensitively."
              >
                <input
                  className="field__input mono"
                  value={form.code}
                  onChange={(e) => set('code', e.target.value.toUpperCase().replace(/\s/g, ''))}
                  maxLength={60}
                  placeholder="AI100FREE"
                />
              </Field>
            ) : (
              <div className="field__row">
                <Field label="Prefix" hint="Optional, e.g. SKX-AI">
                  <input
                    className="field__input mono"
                    value={form.prefix}
                    onChange={(e) =>
                      set('prefix', e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))
                    }
                    maxLength={20}
                  />
                </Field>
                <Field label="Random characters" hint="4–32">
                  <input
                    className="field__input"
                    type="number"
                    min={4}
                    max={32}
                    value={form.codeLength}
                    onChange={(e) =>
                      set('codeLength', Math.max(4, Math.min(32, Number(e.target.value) || 4)))
                    }
                  />
                </Field>
                {form.mode === 'bulk' ? (
                  <Field label="Number of codes" hint="Up to 5,000 per run">
                    <input
                      className="field__input"
                      type="number"
                      min={1}
                      max={5000}
                      value={form.count}
                      onChange={(e) => set('count', Math.round(Number(e.target.value) || 0))}
                    />
                  </Field>
                ) : null}
              </div>
            )}
            {!isEdit && form.mode !== 'manual' ? (
              <p className="field__hint">
                Generated from a cryptographically secure source, using uppercase letters and digits
                only. Look-alike characters (0/O, 1/I/L, U/V) are left out so codes can be read
                aloud and typed without mistakes.
              </p>
            ) : null}
            <Field label="Description" hint="Internal note, e.g. who the code is for.">
              <input
                className="field__input"
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                maxLength={400}
              />
            </Field>
          </>
        ) : null}

        {step === 1 ? (
          <fieldset className="plainset" disabled={locked}>
            {locked ? (
              <Alert
                kind="info"
                title="The discount is locked"
                items={[
                  `${editing?.redemptionCount ?? 0} ${editing?.redemptionCount === 1 ? 'person has' : 'people have'} already redeemed this code, so its discount can no longer change. Everything else can still be edited. For different terms, duplicate it as a new code.`,
                ]}
              />
            ) : null}
            <fieldset className="choices">
              <legend className="field__label">Discount type</legend>
              {(
                [
                  [
                    'FREE_ACCESS',
                    '100% free',
                    'The learner pays nothing and is enrolled immediately.',
                  ],
                  ['PERCENTAGE', 'Percentage off', 'e.g. 25% off, optionally capped.'],
                  ['FIXED_AMOUNT', 'Fixed amount off', 'e.g. ₹2,000 off. Never below ₹0.'],
                ] as const
              ).map(([value, label, hint]) => (
                <label
                  key={value}
                  className={`choice${form.discountType === value ? ' is-on' : ''}`}
                >
                  <input
                    type="radio"
                    name="discountType"
                    checked={form.discountType === value}
                    onChange={() => set('discountType', value)}
                  />
                  <span>
                    <strong>{label}</strong>
                    <span className="muted">{hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            {form.discountType === 'PERCENTAGE' ? (
              <div className="field__row">
                <Field label="Percentage" hint="1–100">
                  <input
                    className="field__input"
                    type="number"
                    min={1}
                    max={100}
                    value={form.percent}
                    onChange={(e) => set('percent', e.target.value)}
                  />
                </Field>
                <Field label="Maximum discount (₹)" hint="Optional cap, e.g. 25% off up to ₹2,000">
                  <input
                    className="field__input"
                    type="number"
                    min={0}
                    value={form.maxDiscountRupees}
                    onChange={(e) => set('maxDiscountRupees', e.target.value)}
                  />
                </Field>
              </div>
            ) : null}
            {form.discountType === 'FIXED_AMOUNT' ? (
              <Field label="Amount off (₹)">
                <input
                  className="field__input"
                  type="number"
                  min={1}
                  value={form.fixedRupees}
                  onChange={(e) => set('fixedRupees', e.target.value)}
                />
              </Field>
            ) : null}
            {form.discountType === 'FREE_ACCESS' ? (
              <Field
                label="Access length (days)"
                hint="Optional. Leave empty for lifetime access, like a normal purchase."
              >
                <input
                  className="field__input"
                  type="number"
                  min={1}
                  value={form.grantDays}
                  onChange={(e) => set('grantDays', e.target.value)}
                />
              </Field>
            ) : null}
          </fieldset>
        ) : null}

        {step === 2 ? (
          <>
            <Field label="Applies to">
              <select
                className="field__input"
                value={form.scopeType}
                onChange={(e) => {
                  set('scopeType', e.target.value as ScopeType);
                  set('scopeIds', []);
                  setPickerFilter('');
                }}
              >
                {(Object.keys(SCOPE_LABEL) as ScopeType[]).map((type) => (
                  <option key={type} value={type}>
                    {SCOPE_LABEL[type]}
                  </option>
                ))}
              </select>
            </Field>
            {form.scopeType === 'ALL' ? (
              <p className="muted">Valid on every eligible course and plan.</p>
            ) : !options ? (
              <div className="empty">Loading…</div>
            ) : (
              <div className="picker">
                <input
                  className="input"
                  placeholder={`Search ${SCOPE_LABEL[form.scopeType].toLowerCase()}`}
                  value={pickerFilter}
                  onChange={(e) => setPickerFilter(e.target.value)}
                />
                <p className="muted picker__count">
                  {form.scopeIds.length} selected
                  {form.scopeType === 'CATEGORY' ||
                  form.scopeType === 'PRODUCT' ||
                  form.scopeType === 'PATH'
                    ? ' · applies to every course inside'
                    : ''}
                </p>
                <ul className="picker__list">
                  {filtered.map((option) => (
                    <li key={option.id}>
                      <label>
                        <input
                          type="checkbox"
                          checked={form.scopeIds.includes(option.id)}
                          onChange={(e) =>
                            set(
                              'scopeIds',
                              e.target.checked
                                ? [...form.scopeIds, option.id]
                                : form.scopeIds.filter((id) => id !== option.id),
                            )
                          }
                        />
                        <span>{option.label}</span>
                        {option.priceMinor !== undefined ? (
                          <span className="muted">
                            {option.priceMinor === 0
                              ? 'Free'
                              : money(option.priceMinor, option.currency ?? 'INR')}
                          </span>
                        ) : null}
                      </label>
                    </li>
                  ))}
                  {filtered.length === 0 ? <li className="muted">Nothing matches.</li> : null}
                </ul>
              </div>
            )}
          </>
        ) : null}

        {step === STEP.who ? (
          <>
            <fieldset className="choices">
              <legend className="field__label">By account</legend>
              {(
                [
                  ['ALL', 'Everyone', 'Any signed-in learner.'],
                  ['NEW_USERS', 'New users only', 'Accounts created recently.'],
                  ['EXISTING_USERS', 'Existing users only', 'Accounts older than that.'],
                ] as const
              ).map(([value, label, hint]) => (
                <label key={value} className={`choice${form.audience === value ? ' is-on' : ''}`}>
                  <input
                    type="radio"
                    name="audience"
                    checked={form.audience === value}
                    onChange={() => set('audience', value)}
                  />
                  <span>
                    <strong>{label}</strong>
                    <span className="muted">{hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            {form.audience !== 'ALL' ? (
              <Field
                label="An account counts as new for (days)"
                hint="Measured from when the account was created to the moment of redemption."
              >
                <input
                  className="field__input"
                  type="number"
                  min={1}
                  value={form.newUserDays}
                  onChange={(e) => set('newUserDays', e.target.value)}
                />
              </Field>
            ) : null}
            <label className="checkline">
              <input
                type="checkbox"
                checked={form.firstPurchaseOnly}
                onChange={(e) => set('firstPurchaseOnly', e.target.checked)}
              />
              First purchase only — the learner must never have completed a paid order
            </label>

            <p className="field__label" style={{ marginTop: 'var(--sx-space-4)' }}>
              Limit to specific people or organizations
            </p>
            <p className="field__hint">
              Optional. Leave all three empty for everyone. When filled, a learner qualifies by
              matching <strong>any one</strong> of them.
            </p>
            <div className="field__row">
              <Field label="Specific people" hint="Email addresses, one per line.">
                <textarea
                  className="field__input"
                  rows={4}
                  value={form.emailsText}
                  onChange={(e) => set('emailsText', e.target.value)}
                  placeholder={'asha@acme.com\nravi@partner.in'}
                />
              </Field>
              <Field label="Email domains" hint="e.g. acme.com — anyone with that address.">
                <textarea
                  className="field__input"
                  rows={4}
                  value={form.domainsText}
                  onChange={(e) => set('domainsText', e.target.value)}
                  placeholder={'acme.com\npartner.in'}
                />
              </Field>
            </div>
            <div className="picker">
              <p className="field__label">Organizations</p>
              <p className="muted picker__count">{form.orgIds.length} selected</p>
              <ul className="picker__list">
                {(options?.ORGANIZATION ?? []).map((org) => (
                  <li key={org.id}>
                    <label>
                      <input
                        type="checkbox"
                        checked={form.orgIds.includes(org.id)}
                        onChange={(e) =>
                          set(
                            'orgIds',
                            e.target.checked
                              ? [...form.orgIds, org.id]
                              : form.orgIds.filter((id) => id !== org.id),
                          )
                        }
                      />
                      <span>{org.label}</span>
                    </label>
                  </li>
                ))}
                {options && options.ORGANIZATION.length === 0 ? (
                  <li className="muted">No customer organizations yet.</li>
                ) : null}
              </ul>
            </div>
            <p className="field__hint">
              A learner who does not qualify sees only “This promo code is not available for your
              account.” — the rule itself is never revealed.
            </p>
          </>
        ) : null}

        {step === STEP.limits ? (
          <>
            <label className="checkline">
              <input
                type="checkbox"
                checked={form.unlimited}
                onChange={(e) => set('unlimited', e.target.checked)}
              />
              Unlimited total redemptions
            </label>
            <div className="field__row">
              {!form.unlimited ? (
                <Field
                  label={form.mode === 'bulk' ? 'Uses per code' : 'Total redemptions'}
                  hint="Across all users"
                >
                  <input
                    className="field__input"
                    type="number"
                    min={1}
                    value={form.maxRedemptions}
                    onChange={(e) => set('maxRedemptions', e.target.value)}
                  />
                </Field>
              ) : null}
              <Field label="Uses per person" hint="0 means no per-person limit">
                <input
                  className="field__input"
                  type="number"
                  min={0}
                  value={form.maxPerUser}
                  onChange={(e) => set('maxPerUser', e.target.value)}
                />
              </Field>
              <Field label="Uses per organization" hint="Optional. Customer organizations only.">
                <input
                  className="field__input"
                  type="number"
                  min={1}
                  value={form.orgLimit}
                  onChange={(e) => set('orgLimit', e.target.value)}
                  placeholder="No limit"
                />
              </Field>
              <Field label="Minimum purchase (₹)" hint="Optional">
                <input
                  className="field__input"
                  type="number"
                  min={0}
                  value={form.minOrderRupees}
                  onChange={(e) => set('minOrderRupees', e.target.value)}
                />
              </Field>
            </div>
            {isEdit && editing && editing.redemptionCount > 0 ? (
              <p className="field__hint">
                Already redeemed {editing.redemptionCount.toLocaleString()} times — the total limit
                cannot be set below that.
              </p>
            ) : null}
            <p className="field__hint">
              A failed or abandoned purchase never uses up a redemption. Only one promo code can be
              applied to an order.
            </p>
          </>
        ) : null}

        {step === STEP.validity ? (
          <>
            <Field label="Timezone" hint="The start and end are interpreted in this timezone.">
              <select
                className="field__input"
                value={form.timezone}
                onChange={(e) => set('timezone', e.target.value)}
              >
                {TIMEZONES.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </Field>
            <div className="field__row">
              <Field label="Start date">
                <input
                  className="field__input"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => set('startDate', e.target.value)}
                />
              </Field>
              <Field label="Start time">
                <input
                  className="field__input"
                  type="time"
                  value={form.startTime}
                  onChange={(e) => set('startTime', e.target.value)}
                />
              </Field>
            </div>
            <label className="checkline">
              <input
                type="checkbox"
                checked={form.hasEnd}
                onChange={(e) => set('hasEnd', e.target.checked)}
              />
              The code ends on a set date
            </label>
            {form.hasEnd ? (
              <div className="field__row">
                <Field label="End date">
                  <input
                    className="field__input"
                    type="date"
                    value={form.endDate}
                    onChange={(e) => set('endDate', e.target.value)}
                  />
                </Field>
                <Field label="End time">
                  <input
                    className="field__input"
                    type="time"
                    value={form.endTime}
                    onChange={(e) => set('endTime', e.target.value)}
                  />
                </Field>
              </div>
            ) : null}
          </>
        ) : null}

        {step === STEP.review ? (
          <dl className="review">
            <ReviewRow label="Code">
              {editing ? (
                <strong className="mono">{editing.code}</strong>
              ) : form.mode === 'manual' ? (
                <strong className="mono">{form.code.trim().toUpperCase() || '—'}</strong>
              ) : form.mode === 'generate' ? (
                `One generated code${form.prefix ? ` starting ${form.prefix}-` : ''}`
              ) : (
                `${form.count.toLocaleString()} generated codes${form.prefix ? ` starting ${form.prefix}-` : ''}`
              )}
            </ReviewRow>
            <ReviewRow label="Campaign">
              {form.campaignId === NEW_CAMPAIGN
                ? `${form.campaignName.trim() || '—'} (new)`
                : (options?.CAMPAIGN.find((c) => c.id === form.campaignId)?.label ?? '—')}
            </ReviewRow>
            <ReviewRow label="Discount">
              {form.discountType === 'FREE_ACCESS'
                ? `100% free${Number(form.grantDays) > 0 ? ` · ${form.grantDays} days of access` : ' · lifetime access'}`
                : form.discountType === 'PERCENTAGE'
                  ? `${form.percent}% off${Number(form.maxDiscountRupees) > 0 ? ` up to ₹${Number(form.maxDiscountRupees).toLocaleString('en-IN')}` : ''}`
                  : `₹${Number(form.fixedRupees || 0).toLocaleString('en-IN')} off`}
            </ReviewRow>
            <ReviewRow label="Applies to">
              {form.scopeType === 'ALL'
                ? 'Entire platform'
                : `${SCOPE_LABEL[form.scopeType]}: ${chosenLabels.join(', ') || '—'}`}
            </ReviewRow>
            <ReviewRow label="Who can redeem">
              {whoText({
                audience: form.audience,
                newUserDays: Number(form.newUserDays) || 30,
                firstPurchaseOnly: form.firstPurchaseOnly,
                emails: listFrom(form.emailsText).length,
                domains: listFrom(form.domainsText).map((d) => d.replace(/^@/, '').toLowerCase()),
                organizations: (options?.ORGANIZATION ?? [])
                  .filter((org) => form.orgIds.includes(org.id))
                  .map((org) => org.label),
              })}
            </ReviewRow>
            <ReviewRow label="Limits">
              {form.unlimited
                ? 'Unlimited redemptions'
                : `${Number(form.maxRedemptions).toLocaleString()} ${form.mode === 'bulk' ? 'per code' : 'in total'}`}
              {' · '}
              {Number(form.maxPerUser) === 0
                ? 'no per-person limit'
                : `${form.maxPerUser} per person`}
              {Number(form.orgLimit) > 0 ? ` · ${form.orgLimit} per organization` : ''}
              {Number(form.minOrderRupees) > 0
                ? ` · minimum purchase ₹${Number(form.minOrderRupees).toLocaleString('en-IN')}`
                : ''}
            </ReviewRow>
            <ReviewRow label="Validity">
              {form.startDate} {form.startTime} →{' '}
              {form.hasEnd ? `${form.endDate || '?'} ${form.endTime}` : 'no end date'} (
              {form.timezone})
            </ReviewRow>
          </dl>
        ) : null}

        <div className="wizard__nav">
          <button
            className="btn btn--ghost"
            disabled={step === 0}
            onClick={() => setStep((value) => value - 1)}
          >
            Back
          </button>
          {step < STEPS.length - 1 ? (
            <button className="btn" onClick={next}>
              Continue
            </button>
          ) : (
            <span className="wizard__submit">
              {!isEdit && form.mode !== 'bulk' ? (
                <button
                  className="btn btn--ghost"
                  disabled={busy}
                  onClick={() => void submit('DRAFT')}
                >
                  Save as draft
                </button>
              ) : null}
              <button className="btn" disabled={busy} onClick={() => void submit('ACTIVE')}>
                {busy
                  ? 'Saving…'
                  : isEdit
                    ? 'Save changes'
                    : form.mode === 'bulk'
                      ? `Generate ${form.count.toLocaleString()} codes`
                      : 'Create promo code'}
              </button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ================================================================ details
function Details({
  code,
  canExport,
  onClose,
  onError,
}: {
  code: PromoCode;
  canExport: boolean;
  onClose: () => void;
  onError: (error: ApiError) => void;
}) {
  const [items, setItems] = useState<Redemption[] | null>(null);

  useEffect(() => {
    setItems(null);
    api<{ items: Redemption[] }>(`/admin/commerce/coupons/${code.id}/redemptions`)
      .then((result) => setItems(result.items))
      .catch((caught) => onError(caught as ApiError));
  }, [code.id, onError]);

  const exportAs = (format: 'csv' | 'xlsx') =>
    void download(
      `/admin/commerce/coupons/${code.id}/export${format === 'xlsx' ? '?format=xlsx' : ''}`,
      `promo-${code.code}.${format}`,
    ).catch((caught) => onError({ message: (caught as Error).message } as ApiError));

  return (
    <div className="card">
      <div className="card__head">
        <span className="card__title">
          <span className="mono">{code.code}</span>{' '}
          <span className={`pill pill--${STATE_PILL[code.state]}`}>{STATE_LABEL[code.state]}</span>
        </span>
        <button className="iconbtn" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="card__body">
        <dl className="review review--compact">
          <ReviewRow label="Discount">{discountText(code)}</ReviewRow>
          <ReviewRow label="Campaign">{code.campaign?.name ?? '—'}</ReviewRow>
          <ReviewRow label="Who can redeem">
            {whoText({
              audience: code.eligibility.audience,
              newUserDays: code.eligibility.newUserDays,
              firstPurchaseOnly: code.eligibility.firstPurchaseOnly,
              emails: code.eligibility.emails.length,
              domains: code.eligibility.domains,
              organizations: code.eligibility.organizationIds.length
                ? [`${code.eligibility.organizationIds.length} organization(s)`]
                : [],
            })}
          </ReviewRow>
          <ReviewRow label="Usage">
            {code.redemptionCount.toLocaleString()} of{' '}
            {code.maxRedemptions === null ? 'unlimited' : code.maxRedemptions.toLocaleString()}
            {' · '}
            {code.maxPerUser === 0 ? 'no per-person limit' : `${code.maxPerUser} per person`}
            {code.maxPerOrganization ? ` · ${code.maxPerOrganization} per organization` : ''}
            {code.minOrderMinor ? ` · minimum ${money(code.minOrderMinor, code.currency)}` : ''}
          </ReviewRow>
          <ReviewRow label="Validity">
            {when(code.validFrom, code.timezone)} →{' '}
            {code.validUntil ? when(code.validUntil, code.timezone) : 'no end date'}
          </ReviewRow>
        </dl>

        <div className="card__head card__head--inner">
          <span className="card__title">Redemptions</span>
          {canExport ? (
            <span className="rowactions">
              <button className="iconbtn" onClick={() => exportAs('csv')}>
                Export CSV
              </button>
              <button className="iconbtn" onClick={() => exportAs('xlsx')}>
                Export Excel
              </button>
            </span>
          ) : null}
        </div>
        {!items ? (
          <div className="empty">Loading…</div>
        ) : items.length === 0 ? (
          <div className="empty">Not redeemed yet.</div>
        ) : (
          <div className="tablewrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Redeemed</th>
                  <th>Learner</th>
                  <th>Purchased</th>
                  <th>Original</th>
                  <th>Discount</th>
                  <th>Paid</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={`${item.orderId}-${index}`}>
                    <td>{when(item.redeemedAt, code.timezone)}</td>
                    <td>
                      {item.userName ?? '—'}
                      <div className="muted">{item.userEmail}</div>
                    </td>
                    <td>{item.items.join(', ')}</td>
                    <td>
                      {item.grossMinor === null ? '—' : money(item.grossMinor, item.currency)}
                    </td>
                    <td>−{money(item.discountMinor, item.currency)}</td>
                    <td>{item.netMinor === null ? '—' : money(item.netMinor, item.currency)}</td>
                    <td>
                      <span
                        className={`pill pill--${item.status === 'REDEEMED' ? 'active' : 'pending'}`}
                      >
                        {item.status === 'REDEEMED'
                          ? 'Redeemed'
                          : item.status.replace(/_/g, ' ').toLowerCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------- little bits
function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field" style={{ flex: 1 }}>
      <span className="field__label">{label}</span>
      {children}
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

function ReviewRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

// ============================================================== campaigns
interface CampaignForm {
  name: string;
  description: string;
  ownerId: string;
  startDate: string;
  endDate: string;
}

/**
 * Campaigns (promo spec §21): each with its own figures — codes generated,
 * redeemed and remaining, total uses, courses reached, discount given and
 * revenue from paid orders.
 */
function Campaigns({
  canCreate,
  canEdit,
  onError,
  onChanged,
  onShowCodes,
}: {
  canCreate: boolean;
  canEdit: boolean;
  onError: (error: ApiError) => void;
  onChanged: () => void;
  onShowCodes: (campaign: Campaign) => void;
}) {
  const [items, setItems] = useState<Campaign[] | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [owners, setOwners] = useState<Array<{ id: string; label: string }>>([]);
  const [editing, setEditing] = useState<{ id: string | null; form: CampaignForm } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(() => {
    api<{ items: Campaign[] }>(`/admin/commerce/campaigns${showArchived ? '?archived=true' : ''}`)
      .then((result) => setItems(result.items))
      .catch((caught) => onError(caught as ApiError));
  }, [showArchived, onError]);
  useEffect(load, [load]);

  useEffect(() => {
    if (!canCreate) return;
    api<{ items: Array<{ id: string; label: string }> }>('/admin/commerce/campaigns/owners')
      .then((result) => setOwners(result.items))
      .catch(() => undefined);
  }, [canCreate]);

  const day = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : null;

  async function save() {
    if (!editing || !editing.form.name.trim()) return;
    setBusy(true);
    try {
      const f = editing.form;
      const body = {
        name: f.name.trim(),
        description: f.description.trim(),
        ...(f.ownerId ? { ownerId: f.ownerId } : {}),
        // Whole days, in India time, as the dashboard shows them.
        startsAt: f.startDate ? `${f.startDate}T00:00:00+05:30` : editing.id ? null : undefined,
        endsAt: f.endDate ? `${f.endDate}T23:59:59+05:30` : editing.id ? null : undefined,
      };
      if (editing.id) {
        await api(`/admin/commerce/campaigns/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        setMessage(`${body.name} was updated.`);
      } else {
        await api('/admin/commerce/campaigns', { method: 'POST', body: JSON.stringify(body) });
        setMessage(`${body.name} was created. Add codes to it from the promo code form.`);
      }
      setEditing(null);
      load();
      onChanged();
    } catch (caught) {
      onError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  async function archive(campaign: Campaign, archived: boolean) {
    try {
      await api(`/admin/commerce/campaigns/${campaign.id}/${archived ? 'archive' : 'restore'}`, {
        method: 'POST',
      });
      setMessage(
        `${campaign.name} was ${archived ? 'archived — its codes keep working' : 'restored'}.`,
      );
      load();
      onChanged();
    } catch (caught) {
      onError(caught as ApiError);
    }
  }

  const toForm = (campaign: Campaign): CampaignForm => ({
    name: campaign.name,
    description: campaign.description ?? '',
    ownerId: campaign.owner?.id ?? '',
    startDate: campaign.startsAt ? isoToZoned(campaign.startsAt, 'Asia/Kolkata').date : '',
    endDate: campaign.endsAt ? isoToZoned(campaign.endsAt, 'Asia/Kolkata').date : '',
  });

  return (
    <>
      {message ? <Alert kind="success" title={message} /> : null}

      {editing ? (
        <div className="card">
          <div className="card__head">
            <span className="card__title">{editing.id ? 'Edit campaign' : 'New campaign'}</span>
            <button className="iconbtn" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
          <div className="card__body wizard">
            <div className="field__row">
              <Field label="Name">
                <input
                  className="field__input"
                  value={editing.form.name}
                  maxLength={160}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })
                  }
                  autoFocus
                />
              </Field>
              <Field label="Owner" hint="Who is accountable for the campaign.">
                <select
                  className="field__input"
                  value={editing.form.ownerId}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, ownerId: e.target.value } })
                  }
                >
                  <option value="">{editing.id ? 'Unchanged' : 'Me'}</option>
                  {owners.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Description" hint="What the campaign is for.">
              <textarea
                className="field__input"
                rows={3}
                maxLength={1000}
                value={editing.form.description}
                onChange={(e) =>
                  setEditing({ ...editing, form: { ...editing.form, description: e.target.value } })
                }
              />
            </Field>
            <div className="field__row">
              <Field label="Starts" hint="Optional. For reporting; each code keeps its own window.">
                <input
                  className="field__input"
                  type="date"
                  value={editing.form.startDate}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, startDate: e.target.value } })
                  }
                />
              </Field>
              <Field label="Ends" hint="Optional.">
                <input
                  className="field__input"
                  type="date"
                  value={editing.form.endDate}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, endDate: e.target.value } })
                  }
                />
              </Field>
            </div>
            <div className="wizard__nav">
              <span />
              <button
                className="btn"
                disabled={busy || !editing.form.name.trim()}
                onClick={() => void save()}
              >
                {busy ? 'Saving…' : editing.id ? 'Save changes' : 'Create campaign'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card__head">
          <span className="card__title">Campaigns</span>
          <span className="rowactions">
            <label className="checkline" style={{ margin: 0 }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
            {canCreate && !editing ? (
              <button
                className="btn btn--sm"
                onClick={() => {
                  setMessage(null);
                  setEditing({
                    id: null,
                    form: { name: '', description: '', ownerId: '', startDate: '', endDate: '' },
                  });
                }}
              >
                New campaign
              </button>
            ) : null}
          </span>
        </div>

        {!items ? (
          <div className="empty">Loading…</div>
        ) : items.length === 0 ? (
          <div className="empty">
            No campaigns yet.
            <br />
            <span className="muted">Group related promo codes to report on them together.</span>
          </div>
        ) : (
          <div className="campaigns">
            {items.map((campaign) => (
              <article
                key={campaign.id}
                className={`campaign${campaign.archived ? ' is-archived' : ''}`}
              >
                <header className="campaign__head">
                  <div>
                    <h3 className="campaign__name">
                      {campaign.name}{' '}
                      {campaign.archived ? (
                        <span className="pill pill--inactive">Archived</span>
                      ) : null}
                    </h3>
                    <p className="muted campaign__meta">
                      {campaign.owner ? `Owner: ${campaign.owner.name}` : 'No owner'}
                      {campaign.startsAt || campaign.endsAt
                        ? ` · ${day(campaign.startsAt) ?? '…'} → ${day(campaign.endsAt) ?? 'open-ended'}`
                        : ''}
                    </p>
                    {campaign.description ? (
                      <p className="campaign__desc">{campaign.description}</p>
                    ) : null}
                  </div>
                  <div className="rowactions">
                    <button className="iconbtn" onClick={() => onShowCodes(campaign)}>
                      View codes
                    </button>
                    {canEdit ? (
                      <>
                        <button
                          className="iconbtn"
                          onClick={() => {
                            setMessage(null);
                            setEditing({ id: campaign.id, form: toForm(campaign) });
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="iconbtn"
                          onClick={() => void archive(campaign, !campaign.archived)}
                        >
                          {campaign.archived ? 'Restore' : 'Archive'}
                        </button>
                      </>
                    ) : null}
                  </div>
                </header>
                <dl className="campaign__figures">
                  <div>
                    <dt>Codes generated</dt>
                    <dd>{campaign.codes.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt>Codes redeemed</dt>
                    <dd>{campaign.redeemedCodes.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt>Remaining</dt>
                    <dd>{campaign.remainingCodes.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt>Redemptions</dt>
                    <dd>{campaign.redemptions.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt>Courses</dt>
                    <dd>{campaign.courses.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt>Total discount</dt>
                    <dd>{money(campaign.discountMinor, 'INR')}</dd>
                  </div>
                  <div>
                    <dt>Revenue</dt>
                    <dd>{money(campaign.revenueMinor, 'INR')}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ================================================================ reports
interface PromoReport {
  range: { from: string; to: string };
  currency: string;
  generatedAt: string;
  performance: {
    codesGenerated: number;
    codesRedeemed: number;
    redemptionRate: number;
    unusedCodes: number;
    expiredCodes: number;
    uses: number;
  };
  financial: {
    orders: number;
    grossSalesMinor: number;
    discountMinor: number;
    taxableMinor: number;
    taxMinor: number;
    netSalesMinor: number;
    freeEnrollments: number;
    awaitingPayment: number;
  };
  users: {
    redeemers: number;
    newUsersAcquired: number;
    existingUserRedemptions: number;
    repeatPurchasers: number;
    newUserDays: number;
  };
  daily: Array<{
    day: string;
    redemptions: number;
    discountMinor: number;
    revenueMinor: number;
    free: number;
  }>;
  courses: Array<{
    title: string;
    enrollments: number;
    freeEnrollments: number;
    listValueMinor: number;
    discountMinor: number;
    revenueMinor: number;
  }>;
  codes: Array<{
    code: string;
    campaign: string | null;
    discountType: DiscountType;
    redemptions: number;
    freeEnrollments: number;
    discountMinor: number;
    revenueMinor: number;
  }>;
}

/** A calendar day in India time, n days before today. */
function istDay(daysAgo: number): string {
  return isoToZoned(new Date(Date.now() - daysAgo * 86_400_000).toISOString(), 'Asia/Kolkata').date;
}

const shortDay = (day: string) =>
  new Date(`${day}T00:00:00+05:30`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Kolkata',
  });

const TYPE_LABEL: Record<DiscountType, string> = {
  FREE_ACCESS: '100% free',
  PERCENTAGE: 'Percentage',
  FIXED_AMOUNT: 'Fixed amount',
};

/**
 * Promotion reports (promo spec §24): performance, financial, course and user
 * impact for a date range and, optionally, one campaign. Every figure comes
 * from the server; each section exports as CSV or Excel.
 */
function PromoReports({
  campaigns,
  canExport,
  onError,
}: {
  campaigns: Option[];
  canExport: boolean;
  onError: (error: ApiError) => void;
}) {
  const [from, setFrom] = useState(istDay(29));
  const [to, setTo] = useState(istDay(0));
  const [campaignId, setCampaignId] = useState('');
  const [section, setSection] = useState('summary');
  const [report, setReport] = useState<PromoReport | null>(null);
  const [loading, setLoading] = useState(false);

  const query = useMemo(() => {
    const params = new URLSearchParams({ from, to });
    if (campaignId) params.set('campaignId', campaignId);
    return params.toString();
  }, [from, to, campaignId]);

  useEffect(() => {
    if (!from || !to || from > to) return;
    let cancelled = false;
    setLoading(true);
    api<PromoReport>(`/admin/commerce/promo-reports?${query}`)
      .then((result) => !cancelled && setReport(result))
      .catch((caught) => !cancelled && onError(caught as ApiError))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [query, from, to, onError]);

  const quick = (days: number) => {
    setFrom(istDay(days - 1));
    setTo(istDay(0));
  };

  const exportAs = (format: 'csv' | 'xlsx') =>
    void download(
      `/admin/commerce/promo-reports/export?${query}&section=${section}${format === 'xlsx' ? '&format=xlsx' : ''}`,
      `promo-${section}.${format}`,
    ).catch((caught) => onError({ message: (caught as Error).message } as ApiError));

  const inr = (minor: number) => money(minor, report?.currency ?? 'INR');
  const f = report?.financial;
  const p = report?.performance;
  const u = report?.users;

  return (
    <>
      <div className="card">
        <div className="toolbar toolbar--report">
          <label className="toolbar__field">
            <span className="muted">From</span>
            <input
              className="input"
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="toolbar__field">
            <span className="muted">To</span>
            <input
              className="input"
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <span className="rowactions">
            {[7, 30, 90].map((days) => (
              <button key={days} className="iconbtn" onClick={() => quick(days)}>
                {days} days
              </button>
            ))}
          </span>
          <select
            className="input"
            aria-label="Campaign"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
          >
            <option value="">All campaigns</option>
            {campaigns.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.label}
              </option>
            ))}
          </select>
          {canExport ? (
            <span className="rowactions toolbar__export">
              <select
                className="input"
                aria-label="Section to export"
                value={section}
                onChange={(e) => setSection(e.target.value)}
              >
                <option value="summary">Summary</option>
                <option value="daily">By day</option>
                <option value="courses">Courses</option>
                <option value="codes">Codes</option>
              </select>
              <button className="iconbtn" onClick={() => exportAs('csv')}>
                CSV
              </button>
              <button className="iconbtn" onClick={() => exportAs('xlsx')}>
                Excel
              </button>
            </span>
          ) : null}
        </div>
        <p className="muted report__note">
          {loading ? 'Updating… ' : ''}Days are India time. Sales are paid orders that used a promo
          code; fully refunded orders are excluded.
        </p>
      </div>

      {!report ? (
        <div className="card">
          <div className="empty">
            {from > to ? 'The start date is after the end date.' : 'Loading…'}
          </div>
        </div>
      ) : (
        <>
          <div className="tiles tiles--report">
            <Tile
              tone="navy"
              icon="₹"
              label="Gross sales"
              value={inr(f!.grossSalesMinor)}
              note={`${f!.orders} orders`}
            />
            <Tile tone="gold" icon="−" label="Discount given" value={inr(f!.discountMinor)} />
            <Tile
              tone="teal"
              icon="↗"
              label="Net sales"
              value={inr(f!.netSalesMinor)}
              note="incl. tax"
            />
            <Tile
              tone="azure"
              icon="🎓"
              label="Free enrollments"
              value={f!.freeEnrollments.toLocaleString()}
            />
            <Tile
              tone="indigo"
              icon="%"
              label="Redemption rate"
              value={`${Math.round(p!.redemptionRate * 100)}%`}
              note={`${p!.codesRedeemed} of ${p!.codesGenerated} codes created in range`}
              progress={p!.redemptionRate * 100}
            />
            <Tile
              tone="navy"
              icon="＋"
              label="New users acquired"
              value={u!.newUsersAcquired.toLocaleString()}
              note={`accounts under ${u!.newUserDays} days`}
            />
          </div>

          <div className="report__charts">
            <div className="card report__chart">
              <BarChart
                title="Redemptions per day"
                integer
                categories={report.daily.map((d) => d.day)}
                categoryLabel={shortDay}
                series={[
                  {
                    key: 'redemptions',
                    label: 'Redemptions',
                    color: 'var(--sx-chart-1)',
                    values: report.daily.map((d) => d.redemptions),
                  },
                ]}
                format={(value) =>
                  Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1)
                }
              />
            </div>
            <div className="card report__chart">
              <BarChart
                title="Revenue and discount per day (₹)"
                categories={report.daily.map((d) => d.day)}
                categoryLabel={shortDay}
                series={[
                  {
                    key: 'revenue',
                    label: 'Revenue',
                    color: 'var(--sx-chart-1)',
                    values: report.daily.map((d) => d.revenueMinor),
                  },
                  {
                    key: 'discount',
                    label: 'Discount',
                    color: 'var(--sx-chart-2)',
                    values: report.daily.map((d) => d.discountMinor),
                  },
                ]}
                format={(minor) => inr(Math.round(minor))}
              />
            </div>
          </div>

          <div className="report__grid">
            <div className="card">
              <div className="card__head">
                <span className="card__title">Financial impact</span>
              </div>
              <div className="card__body">
                <dl className="totals">
                  <div>
                    <dt>Gross sales (list price)</dt>
                    <dd>{inr(f!.grossSalesMinor)}</dd>
                  </div>
                  <div className="totals__discount">
                    <dt>Discount</dt>
                    <dd>−{inr(f!.discountMinor)}</dd>
                  </div>
                  <div>
                    <dt>Taxable amount</dt>
                    <dd>{inr(f!.taxableMinor)}</dd>
                  </div>
                  <div>
                    <dt>Tax</dt>
                    <dd>{inr(f!.taxMinor)}</dd>
                  </div>
                  <div className="totals__total">
                    <dt>Net sales</dt>
                    <dd>{inr(f!.netSalesMinor)}</dd>
                  </div>
                </dl>
                <p className="muted report__note">
                  {f!.freeEnrollments} free enrollments · {f!.awaitingPayment} orders still awaiting
                  payment (not counted)
                </p>
              </div>
            </div>

            <div className="card">
              <div className="card__head">
                <span className="card__title">Promo performance</span>
              </div>
              <div className="card__body">
                <dl className="review review--compact">
                  <ReviewRow label="Codes generated">
                    {p!.codesGenerated.toLocaleString()}
                  </ReviewRow>
                  <ReviewRow label="Codes redeemed">
                    {p!.codesRedeemed.toLocaleString()} ({Math.round(p!.redemptionRate * 100)}%)
                  </ReviewRow>
                  <ReviewRow label="Unused codes">{p!.unusedCodes.toLocaleString()}</ReviewRow>
                  <ReviewRow label="Expired codes">{p!.expiredCodes.toLocaleString()}</ReviewRow>
                </dl>
                <p className="muted report__note">For codes created in the selected range.</p>
              </div>
            </div>

            <div className="card">
              <div className="card__head">
                <span className="card__title">User impact</span>
              </div>
              <div className="card__body">
                <dl className="review review--compact">
                  <ReviewRow label="People who redeemed">{u!.redeemers.toLocaleString()}</ReviewRow>
                  <ReviewRow label="New users acquired">
                    {u!.newUsersAcquired.toLocaleString()}
                  </ReviewRow>
                  <ReviewRow label="Existing-user redemptions">
                    {u!.existingUserRedemptions.toLocaleString()}
                  </ReviewRow>
                  <ReviewRow label="Repeat purchasers">
                    {u!.repeatPurchasers.toLocaleString()}
                  </ReviewRow>
                </dl>
                <p className="muted report__note">
                  New = account under {u!.newUserDays} days old when redeeming. Repeat = paid again
                  after redeeming.
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card__head">
              <span className="card__title">Top promo codes</span>
            </div>
            {report.codes.length === 0 ? (
              <div className="empty">No redemptions in this period.</div>
            ) : (
              <div className="tablewrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Campaign</th>
                      <th>Type</th>
                      <th>Redemptions</th>
                      <th>Free</th>
                      <th>Discount</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.codes.map((row) => (
                      <tr key={row.code}>
                        <td>
                          <strong className="mono">{row.code}</strong>
                        </td>
                        <td>{row.campaign ?? <span className="muted">—</span>}</td>
                        <td>{TYPE_LABEL[row.discountType] ?? row.discountType}</td>
                        <td>{row.redemptions.toLocaleString()}</td>
                        <td>{row.freeEnrollments.toLocaleString()}</td>
                        <td>{inr(row.discountMinor)}</td>
                        <td>{inr(row.revenueMinor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card__head">
              <span className="card__title">Course impact</span>
            </div>
            {report.courses.length === 0 ? (
              <div className="empty">No course enrollments from promotions in this period.</div>
            ) : (
              <div className="tablewrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Enrollments via promotion</th>
                      <th>Free</th>
                      <th>List value</th>
                      <th>Discount</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.courses.map((row) => (
                      <tr key={row.title}>
                        <td>{row.title}</td>
                        <td>{row.enrollments.toLocaleString()}</td>
                        <td>{row.freeEnrollments.toLocaleString()}</td>
                        <td>{inr(row.listValueMinor)}</td>
                        <td>{inr(row.discountMinor)}</td>
                        <td>{inr(row.revenueMinor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
